import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, Subscription, tap } from 'rxjs';
import { NotificationSettings } from 'src/app/model/Setting';
import { UserInfo } from 'src/app/model/User';
import { EventService } from 'src/app/service/event.service';
import { GeneralService } from 'src/app/service/general.service';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { DataTableFilterOption } from '../controls/datatable/datatable.component';
import { ModalControl, ModalControlType, ModalMessageComponent, ModalResult } from '../controls/modal-message/modal-message.component';
import { ModalConfig } from '../controls/modal/modal-config';
import { Modal } from '../controls/modal/modal.service';

@Component({
    selector: 'app-settings-notification',
    templateUrl: './settings-notification.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-notification.component.css'],
    standalone: false
})
export class SettingsNotificationComponent implements OnInit {

  isLoading: boolean = false;
  isSaving: boolean = false;
  savingBtn: string = $localize`Save`;
  types: Map<string, DataTableFilterOption>;
  typeCtrls: Map<string, FormControl> = new Map<string, FormControl>();
  settings: NotificationSettings;
  selectAllText: string = $localize`All`;
  selectAll: boolean = false;
  selectedItems: string[] = [];

  settingsForm: FormGroup;
  checkboxControls: FormControl[];
  currentUserAuthInfo: UserInfo;

  constructor(private genService: GeneralService,
    private eventService: EventService,
    private modal: Modal,
    private fb: FormBuilder) {
    this.checkboxControls = [];
  }

  get isPurgeActiveCtrl() { return this.settingsForm.get('isPurgeActiveCtrl'); }
  get notificationRetentionPeriodCtrl() { return this.settingsForm.get('notificationRetentionPeriodCtrl'); }

  ngOnInit(): void {
    this.initData();
  }

  initData(): void {
    this.isLoading = true;
    this.genService.getNotificationSettings().pipe(tap(resp => {
      this.settings = resp.body;
      this.settingsForm = this.fb.group({
        isPurgeActiveCtrl: false,
        notificationRetentionPeriodCtrl: new FormControl(1, [
          Validators.min(1),
          Validators.max(90),
          Validators.pattern("^[0-9]*$")])
      });
      this.isPurgeActiveCtrl.setValue(this.settings.purge);
      this.notificationRetentionPeriodCtrl.setValue(this.settings.days);
      this.types = Utils.createNotificationEventMap(this.settings.types, this.settings.selected);
      Array.from(this.types.keys()).forEach(key => {
        let check: FormControl = new FormControl(this.types.get(key).selected);
        this.typeCtrls.set(key, check);
        this.settingsForm.addControl(key, check);
      });
      this.settingsForm.reset(this.settingsForm.value);
      this.setSelectAll();
      setTimeout(() => {
        this.activatePurgeChange();
      }, 0);
    }),
      finalize(() => {
        this.isLoading = false;
      }),
    ).subscribe();
  }

  canDeactivate(): boolean {
    return !this.settingsForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-notifications-unsaved-changes');
  }

  activatePurgeChange(): void {
    Array.from(this.typeCtrls.keys()).forEach(key => {
      if (this.isPurgeActiveCtrl.value){
        this.typeCtrls.get(key).enable();
      } else {
        this.typeCtrls.get(key).disable();
      }
    });
  }

  getTypeText(key: string): string {
    return this.types.get(key).text;
  }

  selectAllChange(): void {
    this.selectedItems = [];
    Array.from(this.typeCtrls.keys()).forEach(key => {
      this.typeCtrls.get(key).setValue(this.selectAll);
      if(this.selectAll){
        this.selectedItems.push(key);
      }
    });
    if(!this.settingsForm.dirty){
      this.settingsForm.markAsDirty();
    }
  }

  selectChange(event: any): void {
    this.setSelectAll();
  }

  setSelectAll(): void {
    let selected: boolean = true;
    this.selectedItems = [];
    Array.from(this.typeCtrls.keys()).forEach(key => {
      if (this.typeCtrls.get(key).value) {
        this.selectedItems.push(key);
      } else {
        selected = false;
      }
    });
    this.selectAll = selected;
  }

  save(): void {
    let checkControl: ModalControl = new ModalControl('export-include-files', ModalControlType.CHECKBOX,
      $localize`Purge notifications now`, false);
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Save`,
          message: $localize`The changes made on this page will be saved. Do you want to save?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
          useResult: true,
          controls: this.isPurgeActiveCtrl.value ? [checkControl] : []
        },
        id: 'otsc-settings-notifications-save'
      }
      ).afterClosed.subscribe((result: ModalResult) => {
        if (result.isPrimary) {
          this.isSaving = true;
          this.savingBtn = $localize`Saving...`;
          let selected: string[] = [];
          Array.from(this.typeCtrls.keys()).forEach(key => {
            if (this.typeCtrls.get(key).value) {
              selected.push(key);
            }
          });
          this.settings.purge = this.isPurgeActiveCtrl.value;
          this.settings.days = this.notificationRetentionPeriodCtrl.value;
          this.settings.selected = selected;
          this.settings.purgeNow = this.isPurgeActiveCtrl.value ? result.controls[0].val : false;
          this.genService.updateNotificationSettings(this.settings).pipe(tap(resp => {
            if (resp.body) {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Notification settings were saved successfully`);
            } else {
              Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Notification settings could not be saved. Please try again later.`);
            }
            this.initData();
          }),
            finalize(() => {
              this.isSaving = false;
              this.savingBtn = $localize`Save`;
            }),
          ).subscribe();
        }
        sub.unsubscribe();
      });
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to reset?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-notifications-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.initData();
        }
        sub.unsubscribe();
      });
  }
}
